import { useCallback } from 'react';
import {
  Box,
  IconButton,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Stack,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Today,
  Refresh,
  Fullscreen,
  FullscreenExit,
  UnfoldMore,
  UnfoldLess,
} from '@mui/icons-material';
import { format, subDays } from 'date-fns';
import { LEADING_FORM_CONTROL_LABEL_MARGIN } from '@dispatch/web/ui';
import { SchedulerDateRange } from '../types/scheduler.types';
import { useSchedulerContext } from '../context/SchedulerContext';
import {
  CapacityExpansionScheduler,
  collapseAllCapacityBands,
  expandCapacityBands,
} from '../utils/capacityBandExpansion';
import { WEEK_DAYS } from '../utils/displayWindow';

/** Hour options for the working day start dropdown (0–22; 22 is max so auto-bump keeps end ≤ 23) */
const START_HOUR_OPTIONS = Array.from({ length: 23 }, (_, i) => i);

/** Hour options for the working day end dropdown (1–23) */
const END_HOUR_OPTIONS = Array.from({ length: 23 }, (_, i) => i + 1);

/**
 * Accessible names for the capacity expand / collapse-all controls
 * (ADO #104914). Exported so a test names the control the same way a screen
 * reader does, rather than matching on visible text an icon button has none of.
 */
export const CAPACITY_EXPAND_LABEL = 'Expand Claim Owner capacity';
export const CAPACITY_COLLAPSE_ALL_LABEL = 'Collapse all Claim Owner capacity';

/** Format an hour value (0–23) as a human-readable label, e.g. "5 AM", "12 PM" */
export function formatHourLabel(hour: number): string {
  if (hour === 0) return '12 AM';
  if (hour < 12) return `${hour} AM`;
  if (hour === 12) return '12 PM';
  return `${hour - 12} PM`;
}

interface SchedulerToolbarProps {
  dateRange: SchedulerDateRange;
  /** Days Prev / Next step by: the visible window, which a date filter sets. */
  windowDays: number;
  timeZone: string;
  timeZoneOptions?: { title: string; value: string | number }[];
  onTimeZoneChange: (tz: string) => void;
  onRefresh: () => void;
  onFullScreen: () => void;
  isFullScreen: boolean;
  showNonWorkingTime?: boolean;
  onShowNonWorkingTimeChange?: (show: boolean) => void;
  /**
   * Whether the Claim Owner capacity band is shown (ADO #104914). Starts off on
   * every board load — the toggle is deliberately not persisted.
   */
  showClaimOwnerCapacity?: boolean;
  /**
   * Handler for the capacity switch. Omitted when the tenant flag is off, which
   * is what hides the switch entirely — the same convention
   * `onShowNonWorkingTimeChange` uses for its own switch.
   */
  onShowClaimOwnerCapacityChange?: (show: boolean) => void;
  /** Custom working day start hour (0–23) */
  workingDayStartHour?: number;
  /** Custom working day end hour (1–23; must be > startHour) */
  workingDayEndHour?: number;
  onWorkingDayStartHourChange?: (hour: number) => void;
  onWorkingDayEndHourChange?: (hour: number) => void;
}

export function SchedulerToolbar({
  dateRange,
  windowDays,
  timeZone,
  timeZoneOptions = [],
  onTimeZoneChange,
  onRefresh,
  onFullScreen,
  isFullScreen,
  showNonWorkingTime,
  onShowNonWorkingTimeChange,
  showClaimOwnerCapacity,
  onShowClaimOwnerCapacityChange,
  workingDayStartHour,
  workingDayEndHour,
  onWorkingDayStartHourChange,
  onWorkingDayEndHourChange,
}: SchedulerToolbarProps) {
  const { shiftNext, shiftPrevious, shiftToday, schedulerRef } =
    useSchedulerContext();

  /**
   * CLAIM OWNER CAPACITY BAND (ADO #104914) — the expand / collapse-all gate.
   *
   * Exactly the two conditions that gate the per-row expander control, read
   * through exactly the same props, so there is no second mechanism to keep in
   * sync:
   *
   *  - TENANT FLAG: `onShowClaimOwnerCapacityChange` only reaches this toolbar
   *    when `featureFlags.claimOwnerCapacityBand` is true (SchedulerPage passes
   *    `undefined` otherwise). Its absence is already what hides the capacity
   *    switch, and it now hides these two controls with it.
   *  - USER TOGGLE: `showClaimOwnerCapacity` is SchedulerPage's
   *    `isClaimOwnerCapacityAvailable && showClaimOwnerCapacity`, the same value
   *    SchedulerCore turns into `rowExpander.disabled`. When it is off the
   *    expander column is hidden and no strip can be opened, so controls that
   *    open and close strips must not be on screen either.
   *
   * ANDed again here rather than trusted, so the toolbar is correct in
   * isolation: a flag that resolves late cannot leave live controls behind.
   */
  const canUseCapacityBands =
    Boolean(onShowClaimOwnerCapacityChange) && showClaimOwnerCapacity === true;

  const getCapacityScheduler = useCallback(
    () =>
      (schedulerRef.current?.instance ??
        null) as CapacityExpansionScheduler | null,
    [schedulerRef]
  );

  const handleExpandCapacity = useCallback(() => {
    expandCapacityBands(getCapacityScheduler());
  }, [getCapacityScheduler]);

  const handleCollapseAllCapacity = useCallback(() => {
    collapseAllCapacityBands(getCapacityScheduler());
  }, [getCapacityScheduler]);

  // dateRange.endDate is exclusive (midnight of the day after the last
  // visible day), matching the API's half-open convention. The label
  // should show the inclusive last day — e.g. "May 1 - 7" for a week
  // whose endDate is May 8 00:00 — so subtract a day before formatting.
  const formattedDateRange =
    dateRange.startDate && dateRange.endDate
      ? `${format(dateRange.startDate, 'MMM d')} - ${format(
          subDays(dateRange.endDate, 1),
          'MMM d, yyyy'
        )}`
      : '';

  const stepLabel =
    windowDays === WEEK_DAYS
      ? 'week'
      : windowDays === 1
      ? 'day'
      : `${windowDays} days`;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        py: 1,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      {/* Left: Non-working time filter + Navigation */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={LEADING_FORM_CONTROL_LABEL_MARGIN}
      >
        {onShowNonWorkingTimeChange && (
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showNonWorkingTime ?? true}
                onChange={(e) => onShowNonWorkingTimeChange(e.target.checked)}
              />
            }
            label="Non-working time"
            sx={{ mr: 2 }}
          />
        )}

        {/*
          Claim Owner capacity (ADO #104914). Sits immediately after the
          Non-working time switch so the two display switches read as one
          cluster, and is absent entirely when the tenant flag is off (no
          handler prop).
        */}
        {onShowClaimOwnerCapacityChange && (
          <FormControlLabel
            control={
              <Switch
                size="small"
                slotProps={{ input: { 'aria-label': 'Claim Owner capacity' } }}
                checked={showClaimOwnerCapacity ?? false}
                onChange={(e) =>
                  onShowClaimOwnerCapacityChange(e.target.checked)
                }
              />
            }
            label="Claim Owner capacity"
            sx={{ mr: 2 }}
          />
        )}

        {/*
          Expand / Collapse all (ADO #104914). Both appear only while the tenant
          flag AND the capacity toggle are on — see `canUseCapacityBands` — so a
          toggle-off board stays indistinguishable from a flag-off one.

          THE PAIR MATTERS, not just Expand. Each expanded strip inserts rows
          between one resource's task row and the next, so expanding many
          resources lengthens the path a drag has to travel across the board.
          One-click Collapse all is the stated mitigation for that: strips must
          never be the only way back to a dense board, and per-row collapsing of
          40 rows is not a way back.
        */}
        {canUseCapacityBands && (
          <>
            <Tooltip title="Expand capacity for the selected resources (all listed resources if none are selected)">
              <IconButton
                aria-label={CAPACITY_EXPAND_LABEL}
                onClick={handleExpandCapacity}
                size="small"
              >
                <UnfoldMore />
              </IconButton>
            </Tooltip>
            <Tooltip title="Collapse all capacity">
              <IconButton
                aria-label={CAPACITY_COLLAPSE_ALL_LABEL}
                onClick={handleCollapseAllCapacity}
                size="small"
              >
                <UnfoldLess />
              </IconButton>
            </Tooltip>
          </>
        )}

        {onWorkingDayStartHourChange && onWorkingDayEndHourChange && (
          <>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel id="working-day-start-label">Start</InputLabel>
              <Select
                labelId="working-day-start-label"
                value={workingDayStartHour ?? 5}
                label="Start"
                disabled={showNonWorkingTime === true}
                onChange={(e) =>
                  onWorkingDayStartHourChange(Number(e.target.value))
                }
              >
                {START_HOUR_OPTIONS.map((h) => (
                  <MenuItem key={h} value={h}>
                    {formatHourLabel(h)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel id="working-day-end-label">End</InputLabel>
              <Select
                labelId="working-day-end-label"
                value={workingDayEndHour ?? 21}
                label="End"
                disabled={showNonWorkingTime === true}
                onChange={(e) =>
                  onWorkingDayEndHourChange(Number(e.target.value))
                }
              >
                {END_HOUR_OPTIONS.map((h) => (
                  <MenuItem key={h} value={h}>
                    {formatHourLabel(h)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}

        <Tooltip title={`Previous ${stepLabel}`}>
          <IconButton onClick={shiftPrevious} size="small">
            <ChevronLeft />
          </IconButton>
        </Tooltip>

        <Button
          variant="outlined"
          size="small"
          onClick={shiftToday}
          startIcon={<Today />}
        >
          Today
        </Button>

        <Tooltip title={`Next ${stepLabel}`}>
          <IconButton onClick={shiftNext} size="small">
            <ChevronRight />
          </IconButton>
        </Tooltip>

        <Box
          sx={{
            px: 2,
            py: 0.5,
            bgcolor: 'action.hover',
            borderRadius: 1,
            fontWeight: 500,
          }}
        >
          {formattedDateRange}
        </Box>
      </Stack>

      {/* Right: Controls */}
      <Stack direction="row" spacing={2} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="timezone-label">Timezone</InputLabel>
          <Select
            labelId="timezone-label"
            value={timeZone}
            label="Timezone"
            onChange={(e) => onTimeZoneChange(e.target.value)}
          >
            {timeZoneOptions.map((tz) => (
              <MenuItem key={String(tz.value)} value={String(tz.value)}>
                {tz.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Tooltip title="Refresh">
          <IconButton onClick={onRefresh} size="small">
            <Refresh />
          </IconButton>
        </Tooltip>

        <Tooltip title={isFullScreen ? 'Exit fullscreen' : 'Fullscreen'}>
          <IconButton onClick={onFullScreen} size="small">
            {isFullScreen ? <FullscreenExit /> : <Fullscreen />}
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
}
